import { useEffect, useRef, useState } from 'react';

import { getApiBaseUrl } from '@/app/apiEndpoints';
import type { ApiEndpoint } from '@/app/apiEndpoints';
import { getTokenWithRetry } from '@/app/baseApi';
import { logger } from '@/utils/logger';
import { generatePkceParams, retrieveCodeVerifier, storeCodeVerifier } from '@/utils/pkce';

import { useGetOAuthUrlMutation } from '../api/environmentsApi';

interface UseOAuthFlowParams {
  typeGuid: string;
  tenantId: string;
  appId: string;
  envName: string;
  connectionKey: string;
  apiEndpoint?: 'transformer' | 'sfAgent';
  onOAuthComplete?: () => void;
}

// Salesforce typeGuid for PKCE support
const SALESFORCE_TYPE_GUID = '9090b61412a74d97ad9ed02c5fx7e705';

/**
 * Post auth code to backend. Called from the opener window which has Auth0 tokens.
 * PKCE: Includes code_verifier for Salesforce OAuth compliance (June 2026)
 */
const postAuthCode = async (params: {
  authCode: string;
  authCodeRequestGuid: string;
  environmentId?: number;
  apiEndpoint: ApiEndpoint;
  authCodePath: string;
  codeVerifier?: string;
}): Promise<void> => {
  const baseUrl = getApiBaseUrl(params.apiEndpoint);
  const token = await getTokenWithRetry();
  const url = `${baseUrl}${params.authCodePath}`;

  const body: Record<string, unknown> = {
    authCode: params.authCode,
    authCodeRequestGuid: params.authCodeRequestGuid,
  };
  if (params.environmentId) {
    body.environmentId = params.environmentId;
  }
  // PKCE: Include code_verifier for token exchange
  if (params.codeVerifier) {
    body.codeVerifier = params.codeVerifier;
    logger.info('[OAuth] Including PKCE code_verifier in token exchange');
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'text/plain;charset=UTF-8',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    logger.error('[OAuth] Error response:', errorText);
    throw new Error(`Auth code exchange failed: ${response.status} - ${errorText}`);
  }
};

export const useOAuthFlow = (params: UseOAuthFlowParams) => {
  const [oauthUrl, setOauthUrl] = useState<string | null>(null);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [getOAuthUrl, { isLoading: isGettingUrl }] = useGetOAuthUrlMutation();
  const messageReceivedRef = useRef(false);
  const popupRef = useRef<Window | null>(null);

  useEffect(() => {
    const handleMessage = async (e: MessageEvent) => {
      // Handle auth code from popup - make API call from opener (has Auth0 tokens)
      if (e.data?.lingkOauthMessage === 'authCode') {
        messageReceivedRef.current = true;
        try {
          // PKCE: code_verifier is stored in the opener's sessionStorage (not the popup's).
          const codeVerifier =
            typeof e.data.oauthState === 'string'
              ? (retrieveCodeVerifier(e.data.oauthState) ?? undefined)
              : undefined;
          await postAuthCode({
            authCode: e.data.authCode,
            authCodeRequestGuid: e.data.authCodeRequestGuid,
            environmentId: e.data.environmentId,
            apiEndpoint: e.data.apiEndpoint,
            authCodePath: e.data.authCodePath,
            codeVerifier: codeVerifier || undefined,
          });
          setIsAuthorizing(false);
          setOauthUrl(null);
          params.onOAuthComplete?.();
        } catch (err) {
          logger.error('[OAuth] Failed to exchange auth code:', err);
          setIsAuthorizing(false);
          setOauthUrl(null);
        }
        return;
      }
      if (e.data?.lingkOauthMessage === 'complete') {
        messageReceivedRef.current = true;
        setIsAuthorizing(false);
        setOauthUrl(null);
        params.onOAuthComplete?.();
      }
      if (e.data?.lingkOauthMessage === 'failed') {
        messageReceivedRef.current = true;
        setIsAuthorizing(false);
        setOauthUrl(null);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [params.onOAuthComplete]);

  // Fallback: poll for popup close.
  // The popup may be on a different origin (e.g. dataprotect.lingkapps.com when
  // developing on localhost), so postMessage won't reach us. When the popup
  // closes without us having received a message, optimistically assume success
  // and trigger onOAuthComplete so credentials are fetched.
  useEffect(() => {
    if (!isAuthorizing || !popupRef.current) return;

    const interval = setInterval(() => {
      if (popupRef.current?.closed) {
        clearInterval(interval);
        if (!messageReceivedRef.current) {
          setIsAuthorizing(false);
          setOauthUrl(null);
          params.onOAuthComplete?.();
        }
      }
    }, 500);

    return () => clearInterval(interval);
  }, [isAuthorizing, params.onOAuthComplete]);

  const generateUrl = async (orgType: string) => {
    try {
      // Generate PKCE parameters for Salesforce OAuth (required after June 25, 2026)
      let codeChallenge: string | undefined;
      if (params.typeGuid === SALESFORCE_TYPE_GUID) {
        const pkce = await generatePkceParams();
        codeChallenge = pkce.codeChallenge;
        // Store code_verifier in opener sessionStorage; retrieved on postMessage callback
        // using the OAuth state parameter from the popup redirect URL.
        sessionStorage.setItem('pkce_code_verifier_pending', pkce.codeVerifier);
        logger.info('[useOAuthFlow] Generated PKCE parameters for Salesforce OAuth');
      }

      const result = await getOAuthUrl({
        ...params,
        orgType,
        codeChallenge,
      }).unwrap();
      if (result.url) {
        // Extract state from the URL to properly key the code_verifier
        const urlObj = new URL(result.url);
        const state = urlObj.searchParams.get('state');
        if (state && codeChallenge) {
          const pendingVerifier = sessionStorage.getItem('pkce_code_verifier_pending');
          if (pendingVerifier) {
            storeCodeVerifier(state, pendingVerifier);
            sessionStorage.removeItem('pkce_code_verifier_pending');
            logger.info('[useOAuthFlow] Stored code_verifier with state:', state.substring(0, 8));
          }
        }
        setOauthUrl(result.url);
      } else {
        logger.warn('[useOAuthFlow] No URL in result');
      }
    } catch (err) {
      logger.error('[useOAuthFlow] Failed to generate OAuth URL:', err);
    }
  };

  const startOauth = () => {
    if (!oauthUrl) {
      logger.warn('[useOAuthFlow] Cannot start OAuth, no URL available');
      return;
    }
    messageReceivedRef.current = false;
    setIsAuthorizing(true);
    popupRef.current = openOAuthPopup(oauthUrl);
    popupRef.current?.focus();
  };

  return {
    oauthUrl,
    isAuthorizing,
    isGettingUrl,
    generateUrl,
    startOauth,
  };
};

const openOAuthPopup = (url: string) => {
  const width = 600;
  const height = 600;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2.5;
  return window.open(url, 'Oauth', `width=${width},height=${height},top=${top},left=${left}`);
};
